import { IReceipt, ReceiptRow } from '@interfaces/models/receipt.interface';
import { CitizenshipStatusEnum, SuffixValuesEnum } from '@interfaces/open-account/enums';
import { FlowTypes, Move_To_New, TransactionTypesEnum, urlMapper } from '@shared/models';
import { ClearData } from '@shared/models/prospect-data-models.interface';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';
import {
  getIsClient,
  getIsExecutor,
  getIsOnlyTrustee,
  getIsSingleExecutor,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import {
  Gender,
  VgEmploymentState,
  VgimAddress,
  VgPersonalAddressState,
} from 'vg-interaction-model';
import { IFinancialsState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/financials.reducer';
import { IInvestmentObjectivesState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/investment-objectives.reducer';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

import { joinWithSpace } from '../cashier-nonretirement';

interface Profile {
  header: string;
  editable: boolean;
  editRoute: string;
  sectionEvidenceID: string;
  rows: ReceiptRow[];
}

const config = getConfigClientFromRTE();

export function mapGenderToReceiptValue(gender: Gender | string): string {
  if (!gender) {
    return 'Unknown';
  }

  switch (gender.toLowerCase()) {
    case 'male':
    case 'm':
      return 'Male';
    case 'female':
    case 'f':
      return 'Female';
    case 'undisclosed':
    case 'u':
      return 'Undisclosed';
    default:
      return 'Unknown';
  }
}

export function isSameAddress(firstAddress: VgimAddress, secondAddress: VgimAddress): boolean {
  if (!firstAddress || !secondAddress) {
    return false;
  }

  const firstAddressKeys = Object.keys(firstAddress);
  const secondAddressKeys = Object.keys(secondAddress);

  if (firstAddressKeys.length !== secondAddressKeys.length) {
    return false;
  }

  for (const key of firstAddressKeys) {
    if (!(key in secondAddress)) {
      return false;
    }
    if (Array.isArray(firstAddress[key]) && Array.isArray(secondAddress[key])) {
      if (firstAddress[key].length !== secondAddress[key].length) {
        return false;
      }
      for (let i = 0; i < firstAddress[key].length; i++) {
        if (firstAddress[key][i] !== secondAddress[key][i]) {
          return false;
        }
      }
    } else if (!Array.isArray(firstAddress[key]) && !Array.isArray(secondAddress[key])) {
      if (firstAddress[key] !== secondAddress[key]) {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

export function safeAttributeAccessor(value: string): string {
  return value ? value : '';
}

export function splitClientId(id: string) {
  return id.slice(id.length - 4);
}

export function profileDescriptionLink() {
  const link = config.pretty_ecs_domain + config.account_setting_endpoint;
  return ReviewContent.profileDescription.replace('{{link}}', link);
}
export function safeRowBuilder(
  rows: ReceiptRow[],
  label: string,
  value: string,
  evidenceID: string,
) {
  if (value) {
    rows.push({
      label: label,
      values: [value],
      evidenceID: evidenceID,
    });
  }
}

export function safeNameBuilder(
  rows: ReceiptRow[],
  label: string,
  personalInfo: IPersonalInfoClientState['clientData'],
  evidenceID: string,
) {
  if (personalInfo.firstName) {
    const name = joinWithSpace([
      personalInfo.firstName,
      personalInfo.middleName,
      personalInfo.lastName,
      SuffixValuesEnum[personalInfo.suffix],
    ]);
    rows.push({
      label: label,
      values: [name],
      evidenceID: evidenceID,
    });
  }
}

// eslint-disable-next-line max-params, max-statements
export function buildProfileReceipt(
  triageState: TriageState,
  transactionType: TransactionTypesEnum,
  personalInfo: IPersonalInfoClientState['clientData'],
  ssn_private: string,
  address: VgPersonalAddressState,
  phone: IPersonalPhoneState,
  clearData: ClearData,
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.profile,
): IReceipt {
  const flow = urlMapper[triageState.flowType];
  const isExecutor = getIsExecutor(triageState);
  const isSingleExecutor = getIsSingleExecutor(triageState);
  const isSingleTrustee = getIsOnlyTrustee(triageState);
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);
  const isMultiInheritanceFlow = triageState.flowType === FlowTypes.MULTI_INHERITANCE;
  const client: boolean = getIsClient(triageState);
  let header = '';

  if (isMultiInheritanceFlow) {
    header = 'Profile';
  } else if (isExecutor) {
    header = 'Executor';
  } else if (isSingleTrustee) {
    header = 'Trustee Information';
  } else if (isTrustToTrustFlow) {
    header = 'Personal Information';
  } else {
    header = content?.profileLabel || 'Profile';
  }

  const profile = {
    header: header,
    editable: editable && client,
    editRoute: `secure/${flow}/personal-info`,
    sectionEvidenceID: 'profile',
    rows: [],
  };
  if (Move_To_New.has(transactionType) || isMultiInheritanceFlow) {
    buildNewAccountProfile(
      profile,
      personalInfo,
      ssn_private,
      address,
      phone,
      editable,
      client,
      isTrustToTrustFlow || false,
      isSingleExecutor,
      triageState.flowType,
      content,
    );
  } else {
    profile.editable = false;
    safeNameBuilder(profile.rows, content?.nameLabel || 'Name', personalInfo, 'profileName');
  }
  if (triageState.isSpouse && !isMultiInheritanceFlow) {
    profile.rows.push({
      label: 'Relationship',
      values: [triageState.isSpouse === 'YES' ? 'Spouse' : 'Nonspouse'],
      evidenceID: 'profileRelationship',
    });
  }
  if (clearData?.uploadedFilename) {
    profile.rows.push({
      label: 'Document for verification',
      values: [clearData.uploadedFilename],
      evidenceID: 'clearDocumentVerification',
    });
  }

  return profile;
}

function buildNewAccountProfile(
  profile: Profile,
  personalInfo: IPersonalInfoClientState['clientData'],
  ssn_private: string,
  address: VgPersonalAddressState,
  phone: IPersonalPhoneState,
  editable: boolean,
  client: boolean,
  isTrustToTrustFlow: boolean,
  isSingleExecutor: boolean,
  flow: FlowTypes,
  content?: typeof ReviewContent.receiptContent.profile,
) {
  const isMultiInheritance = flow === FlowTypes.MULTI_INHERITANCE;
  profile['description'] =
    (editable && client && !isTrustToTrustFlow) || isSingleExecutor
      ? profileDescriptionLink()
      : null;
  if (isMultiInheritance) {
    profile['description'] = null;
  }
  safeNameBuilder(profile.rows, content?.nameLabel || 'Name', personalInfo, 'profileName');
  safeRowBuilder(
    profile.rows,
    content?.emailLabel || 'Email',
    safeAttributeAccessor(personalInfo.email),
    'profileEmail',
  );
  safeRowBuilder(
    profile.rows,
    content?.dateOfBirthLabel || 'Date of birth',
    safeAttributeAccessor(personalInfo.birthDate),
    'profileDob',
  );
  if (personalInfo.citizenshipStatus) {
    let citizenship = 'Non-resident alien';
    switch (personalInfo.citizenshipStatus) {
      case CitizenshipStatusEnum.UsCitizen:
        citizenship = 'U.S. citizen';
        break;
      case CitizenshipStatusEnum.ResidentAlien:
        citizenship = 'Resident alien';
        break;
    }
    safeRowBuilder(
      profile.rows,
      content?.citizenshipLabel || 'Citizenship',
      safeAttributeAccessor(citizenship),
      'profileCitizenship',
    );
    if (!isTrustToTrustFlow && !isMultiInheritance) {
      safeRowBuilder(
        profile.rows,
        content?.countryOfCitizenshipLabel || 'Country of citizenship',
        personalInfo.countryOfResidence ? personalInfo.countryOfResidence : 'US',
        'profileCountryOfCitizenship',
      );
    }
  }
  if (editable) {
    safeRowBuilder(
      profile.rows,
      content?.socialSecurityNoLabel || 'Social Security number',
      ssn_private,
      'profileSsn-private',
    );
  } else {
    safeRowBuilder(
      profile.rows,
      content?.socialSecurityNoLabel || 'Social Security number',
      '***-**-' + splitClientId(ssn_private),
      'profileSsn-private',
    );
  }
  if (isMultiInheritance) {
    safeRowBuilder(
      profile.rows,
      'Gender',
      mapGenderToReceiptValue(personalInfo.gender),
      'profileGender',
    );
  }
  const primaryResidenceLabel = isTrustToTrustFlow ? 'Address' : 'Primary residence';
  buildReceiptAddress(
    profile.rows,
    primaryResidenceLabel,
    address.personalAddress.streetAddress,
    'profilePrimaryResidenceAddress',
  );

  if (!isTrustToTrustFlow) {
    buildReceiptAddress(
      profile.rows,
      'Mailing address',
      address.personalAddress.mailingAddress,
      'profileMailingAddr',
      isMultiInheritance &&
        isSameAddress(
          address.personalAddress.streetAddress,
          address.personalAddress.mailingAddress,
        ),
    );
  }
  const primaryPhoneLabel = isTrustToTrustFlow ? 'Phone' : 'Primary phone';
  buildReceiptPhone(profile.rows, primaryPhoneLabel, phone, 'profilePrimaryPhone');
}

export function buildReceiptAddress(
  rows: ReceiptRow[],
  label: string,
  addrType: VgimAddress,
  evidenceID: string,
  isSameAddress: boolean = false,
) {
  if (addrType && addrType.street) {
    rows.push({
      label: label,
      values: isSameAddress
        ? ['Same as primary residence']
        : [addrType.street, addrType.city + ', ' + addrType.state + ' ' + addrType.zipCode],
      evidenceID: evidenceID,
    });
  }
}

export function buildReceiptPhone(
  rows: ReceiptRow[],
  label: string,
  phone: IPersonalPhoneState,
  evidenceID: string,
) {
  if (phone?.phoneNumber) {
    const isMobile = phone.isMobile ? ' (Cell phone)' : '';
    rows.push({
      label: label,
      values: [phone.phoneNumber + isMobile],
      evidenceID: evidenceID,
    });
  }
}

export function buildEmploymentReceipt(
  state: VgEmploymentState,
  transactionType: TransactionTypesEnum,
  internalTransferTransactionType: TransactionTypesEnum,
  triageState: TriageState,
  letterUploadState: ILetterUploadState,
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.employment,
): IReceipt {
  if (
    transactionType === TransactionTypesEnum.EXISTING ||
    internalTransferTransactionType === TransactionTypesEnum.EXISTING
  ) {
    return null;
  }

  const statusCode = state.employmentStatus.employmentStatus;
  let statusCodeText: string;
  const businessAddressLine1 = state.employmentStatus.address.street;
  const businessAddressLine2 =
    state.employmentStatus.address.city +
    ', ' +
    state.employmentStatus.address.state +
    ' ' +
    state.employmentStatus.address.zipCode;
  const employmentStatusRows = [
    {
      label: content?.employerLabel || 'Employer',
      values: [
        state.employmentStatus.employmentStatus === 'VE'
          ? 'Vanguard'
          : state.employmentStatus.employer,
      ],
      evidenceID: 'employmentEmployer',
    },
    {
      label: content?.occupationLabel || 'Occupation',
      values: [state.employmentStatus.occupation],
      evidenceID: 'employmentOccupation',
    },
    {
      label: content?.businessStreetAddressLabel || 'Business street address',
      values: [businessAddressLine1, businessAddressLine2],
      evidenceID: 'employmentAddress',
    },
  ];

  const selfEmployedRows = [
    {
      label: content?.companyNameLabel || 'Company name',
      values: [state.employmentStatus.employer],
      evidenceID: 'employmentEmployer',
    },
    {
      label: content?.natureOfBusinessLabel || 'Nature of business',
      values: [state.employmentStatus.occupation],
      evidenceID: 'employmentOccupation',
    },
  ];

  const associationsRows = [
    {
      label: content?.spouseIsVgEmployeeLabel || 'Spouse is a Vanguard employee',
      values: [state.associations.spouse ? 'Yes' : 'No'],
      evidenceID: 'employmentSpouseIsVanguardEmployee',
    },
    {
      label: content?.associatedPersonLabel || 'Associated person',
      values: [state.associations.associated ? 'Yes' : 'No'],
      evidenceID: 'employmentAssociatedPerson',
    },
  ];

  const letterFileName = letterUploadState.filesMetaData[0]?.fileName;
  if (letterUploadState.vgHasLetter || letterFileName) {
    associationsRows.push({
      label: 'Account approval letter',
      values: [letterUploadState.vgHasLetter ? 'On file' : letterFileName],
      evidenceID: 'employmentApprovalLetter',
    });
  }

  associationsRows.push({
    label: content?.controlPersonLabel || 'Control person',
    values: [state.associations.control ? 'Yes' : 'No'],
    evidenceID: 'employmentControlPerson',
  });

  let controlCompaniesRows = [];
  if (state.associations.control) {
    const companies = [];
    state.associations.controlCompanyTicker.forEach((symbol, ind) => {
      companies.push(`${symbol}-${state.associations.controlCompanyName[ind]}`);
    });
    controlCompaniesRows = [
      {
        label: 'Control companies',
        values: [...companies],
        evidenceID: 'controlPersonCompanies',
      },
    ];
  }

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: content?.employmentLabel || 'Employment',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/employment`,
    sectionEvidenceID: 'employment',
    rows: [
      {
        label: 'Status',
        values: [],
        evidenceID: 'employmentStatus',
      },
    ],
  };

  switch (statusCode) {
    case 'E':
      statusCodeText = 'Employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...employmentStatusRows, ...associationsRows, ...controlCompaniesRows);
      break;
    case 'VE':
      statusCodeText = 'Employed by Vanguard';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(employmentStatusRows[1], ...associationsRows, ...controlCompaniesRows);
      break;
    case 'SE':
      statusCodeText = 'Self-employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(
        ...selfEmployedRows,
        employmentStatusRows[2],
        ...associationsRows,
        ...controlCompaniesRows,
      );
      break;
    case 'R':
      statusCodeText = 'Retired';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...associationsRows, ...controlCompaniesRows);
      break;
    case 'U':
      statusCodeText = 'Not employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...associationsRows, ...controlCompaniesRows);
      break;
    default:
      break;
  }
  return receipt;
}

export function buildInvestmentObjectivesDescription(invObj: IInvestmentObjectivesState) {
  let objectives = '';
  const objectiveKeys = Object.keys(invObj.objectives);
  objectiveKeys.forEach((key) => {
    if (invObj.objectives[key]) {
      const label = key === 'CAP' ? 'Capital preservation' : key;
      objectives += label + '<br>';
    }
  });
  return objectives;
}

export function buildFinancialProfileReceipt(
  financial: IFinancialsState,
  invObj: IInvestmentObjectivesState,
  editable: boolean,
  transactionType: TransactionTypesEnum,
  internalTransferTransactionType: TransactionTypesEnum,
  triageState: TriageState,
  content?: typeof ReviewContent.receiptContent.financialProfile,
) {
  if (
    transactionType === TransactionTypesEnum.EXISTING ||
    internalTransferTransactionType === TransactionTypesEnum.EXISTING
  ) {
    return null;
  }
  const flow = urlMapper[triageState.flowType];

  const moneyOptions = {
    G: 'Less than $20,000',
    H: '$20,000 - $49,999',
    I: '$50,000 - $74,999',
    J: '$75,000 - $99,999',
    K: '$100,000 - $499,999',
    L: '$500,000 - $999,999',
    M: '$1,000,000 or above',
  };
  const objectives: string = buildInvestmentObjectivesDescription(invObj);
  const isExecutor = getIsExecutor(triageState);
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);

  const financialProfileReceipt: IReceipt = {
    header: content?.financialProfileLabel || 'Financial profile',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/${isExecutor || isTrustToTrustFlow ? 'investment-objectives' : 'income-net-worth'}`,
    sectionEvidenceID: 'financialProfile',
    rows: [
      {
        label: content?.investmentObjectiveLabel || 'Investment objective',
        values: [objectives],
        evidenceID: 'investmentObjectives',
      },
    ],
  };

  if (financial.annualIncome) {
    financialProfileReceipt.rows.push(
      {
        label: content?.incomeLabel || 'Income',
        values: [moneyOptions[financial.annualIncome]],
        evidenceID: 'income',
      },
      {
        label: content?.netWorthLabel || 'Net worth',
        values: [moneyOptions[financial.liquidNetWorth]],
        evidenceID: 'networth',
      },
    );
  }
  return financialProfileReceipt;
}
